import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown, faSolarPanel, faBolt, faBatteryFull, faSliders,
  faMinus, faPlus, faArrowsUpDown, faArrowUp, faArrowDown,
} from '@fortawesome/free-solid-svg-icons';
import { panelManufacturers, inverterManufacturers, magazineManufacturers } from '../../filtersSchema';
import MultiSelect from './MultiSelect';
import ModeToggle from './ModeToggle';
import { fetchSeriesOptions } from '../../api/fetchProducts';
import { BramkaTresci } from '../auth/BramkaLogowania';
import { useTranslation } from 'react-i18next';
import { KRAJ } from '../../kraj';

// category ('panels'/'inverter'/'battery') -> productType oczekiwany przez
// fetchSeriesOptions ('panel'/'inwerter'/'magazyn') — te same nazwy co na
// listingach produktów, inne niż nazwy kategorii w asystencie doboru.
const PRODUCT_TYPE = { panels: 'panel', inverter: 'inwerter', battery: 'magazyn' };

// Wartości enumów 1:1 z listingiem (te same nazwy parametrów i wartości co API).
const MANUFACTURERS = {
  panels: Object.keys(panelManufacturers),
  inverter: Object.keys(inverterManufacturers),
  // Magazyny mają własnych producentów (Dyness, Pylontech, BYD... robią tylko baterie).
  battery: Object.keys(magazineManufacturers),
};
const PANEL_TYPES = ['Standard', 'Full Black', 'Bifacial'];
const INVERTER_PHASES = ['1 Fazowy', '3 Fazowy'];
const BATTERY_VOLTAGES = ['Wysokonapięciowy', 'Niskonapięciowy'];
const FRAME_COLOURS = ['Czarna rama', 'Srebrna rama'];

// Tytuł sekcji ze słownika: sekcja.naglowek.<kategoria>.
const HEAD = {
  panels: { icon: faSolarPanel },
  inverter: { icon: faBolt },
  battery: { icon: faBatteryFull },
};

const numInput = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50/60 focus:outline-none focus:bg-white focus-visible:ring-2 focus-visible:ring-blue-500';
const fieldLabel = 'block text-xs font-medium text-gray-500 mb-1';

// Grupa przycisków (segmenty) — do kierunku tolerancji, grubości, MPPT itp.
const Segmented = ({ options, value, onChange, ariaLabel }) => (
  <div role="group" aria-label={ariaLabel} className="inline-flex rounded-lg border border-gray-200 overflow-hidden bg-white">
    {options.map((o, i) => {
      const active = value === o.value;
      return (
        <button
          key={o.value ?? 'null'}
          type="button"
          onClick={() => onChange(o.value)}
          title={o.title}
          aria-pressed={active}
          className={`px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${i > 0 ? 'border-l border-gray-200' : ''} ${active ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
        >
          {o.icon ? <FontAwesomeIcon icon={o.icon} /> : o.label}
        </button>
      );
    })}
  </div>
);

// Licznik ze strzałkami (± krok). Pozwala też wpisać wartość ręcznie.
const Stepper = ({ value, onChange, step = 5, min = 0 }) => {
  const { t } = useTranslation('asystent');
  return (
  <div className="inline-flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white">
    <button type="button" aria-label={t('sekcja.licznik.mniej')} onClick={() => onChange(Math.max(min, (Number(value) || 0) - step))} className="px-3 py-2 text-gray-500 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
      <FontAwesomeIcon icon={faMinus} className="text-xs" />
    </button>
    <input type="number" inputMode="numeric" step={step} value={value} onChange={(e) => onChange(e.target.value)} className="w-14 text-center text-sm border-x border-gray-200 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" aria-label={t('sekcja.licznik.wartosc')} />
    <button type="button" aria-label={t('sekcja.licznik.wiecej')} onClick={() => onChange((Number(value) || 0) + step)} className="px-3 py-2 text-gray-500 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
      <FontAwesomeIcon icon={faPlus} className="text-xs" />
    </button>
  </div>
  );
};

const Modeable = ({ label, mode, onMode, children }) => (
  <div>
    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 mb-1">
      <label className={fieldLabel + ' mb-0'}>{label}</label>
      <ModeToggle value={mode} onChange={onMode} label={label} />
    </div>
    {children}
  </div>
);

// `title` to klucz słownika (sekcja.kierunek.*), tłumaczony przy renderze.
const DIR_OPTIONS = [
  { value: 'both', icon: faArrowsUpDown, title: 'obie' },
  { value: 'up', icon: faArrowUp, title: 'wyzsza_moc' },
  { value: 'down', icon: faArrowDown, title: 'nizsza_moc' },
];
// Skoki co 5 kWh, bo tak układają się realne instalacje domowe: 5 kWh do
// zestawu bez pompy, 10 pod pompę, 15-20 przy większym zużyciu. Wartość
// ustawia POJEMNOŚĆ, a nie tolerancję — luz wokół niej robi osobny margines.
const POJEMNOSCI_SKROTY = [5, 10, 15, 20];

const DIR_POJEMNOSCI = [
  { value: 'both', icon: faArrowsUpDown, title: 'obie' },
  { value: 'up', icon: faArrowUp, title: 'wieksza_pojemnosc' },
  { value: 'down', icon: faArrowDown, title: 'mniejsza_pojemnosc' },
];

/**
 * Zdanie pod polami pojemności: mówi, CZEGO NAPRAWDĘ SZUKAMY po doliczeniu
 * marginesu. Bez tego użytkownik wpisuje 10 i 10, a wyniki pokazują 8 i 12 —
 * i nie ma jak połączyć jednego z drugim.
 *
 * Sztywne ±0,5 kWh (potoczne „5 kWh" to często 5,12) WCHODZI DO TEJ LICZBY.
 * Zdanie ma opisywać zapytanie, które naprawdę wychodzi — pomijanie tego
 * półkilowatogodzinowego zapasu dawało podpowiedź „10–12 kWh" przy żądaniu
 * 9,5–12,5 i nie było jak połączyć jednego z drugim.
 */
const KOREKTA_NOMINALU = 0.5;

const opisMarginesu = (state, t) => {
  const liczba = (v) => String(Number(v).toFixed(2)).replace(/\.?0+$/, '').replace('.', ',');
  const m = Number(state.capacity_tolerance_kwh);
  const min = Number(state.capacity_kwh_min);
  const max = Number(state.capacity_kwh_max);
  if (!Number.isFinite(m) || m <= 0) {
    return t('sekcja.margines.drobne');
  }
  const kierunek = state.tolerance_direction || 'both';
  const dol = (kierunek === 'up' ? 0 : m) + KOREKTA_NOMINALU;
  const gora = (kierunek === 'down' ? 0 : m) + KOREKTA_NOMINALU;
  const maMin = Number.isFinite(min) && min > 0;
  const maMax = Number.isFinite(max) && max > 0;
  if (!maMin && !maMax) {
    return t('sekcja.margines.doliczymy', { m: liczba(m) });
  }
  const odKwoty = maMin ? liczba(Math.max(0, min - dol)) : null;
  const doKwoty = maMax ? liczba(max + gora) : null;
  if (odKwoty && doKwoty) return t('sekcja.margines.zakres', { od: odKwoty, do: doKwoty });
  if (odKwoty) return t('sekcja.margines.od', { od: odKwoty });
  return t('sekcja.margines.do', { do: doKwoty });
};

// Etykieta 'dowolna' podmieniana przy renderze na tekst ze słownika.
const THICKNESS_OPTIONS = [
  { value: '', label: 'dowolna' },
  { value: '30', label: '30 mm' },
  { value: 'above_30', label: '> 30 mm' },
];
const MPPT_OPTIONS = [
  { value: '', label: 'dowolna' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3+' },
];

const CategorySection = ({ category, state, dispatch, batterySelected }) => {
  const { t } = useTranslation('asystent');
  // Wartości z bazy (rodzaj, fazy, napięcie, rama) zostają w filtrach bez zmian,
  // tłumaczymy tylko etykietę.
  const wart = (v) => (v ? t(`wartosci.${v}`, { defaultValue: v }) : v);
  const dowolna = t('sekcja.dowolna');
  const zTlumaczeniem = (opcje) => opcje.map((o) => ({ ...o, title: t(`sekcja.kierunek.${o.title}`) }));
  const zDowolna = (opcje) => opcje.map((o) => (o.value === '' ? { ...o, label: dowolna } : o));
  const head = HEAD[category];
  const setField = (key, value) => dispatch({ type: 'SET_FIELD', category, key, value });
  const setFilter = (key, value) => dispatch({ type: 'SET_FILTER', category, key, value });
  const setMode = (key, mode) => dispatch({ type: 'SET_MODE', category, key, mode });
  const f = state.filters;
  const m = state.modes;

  // ---- Seria — dochodzi TYLKO przy dokładnie jednym wybranym producencie
  // (patrz Filters.jsx na listingach — ten sam mechanizm). ZAWSZE twardy
  // filtr, bez ModeToggle: seria nie jest kolumną SQL, więc nie da się jej
  // ocenić w scoringu tak jak pozostałych pól (kits/candidates.py).
  const jedynyProducent = f.manufacturer_in?.length === 1 ? f.manufacturer_in[0] : null
  const [seriesOptions, setSeriesOptions] = useState([])
  const [seriesLoading, setSeriesLoading] = useState(false)

  useEffect(() => {
    if (!jedynyProducent) { setSeriesOptions([]); return undefined }
    let anulowane = false
    setSeriesLoading(true)
    fetchSeriesOptions(PRODUCT_TYPE[category], jedynyProducent).then((opcje) => {
      if (anulowane) return
      setSeriesOptions(opcje)
      setSeriesLoading(false)
    })
    return () => { anulowane = true }
  }, [category, jedynyProducent])

  const poprzedniProducent = useRef(jedynyProducent)
  useEffect(() => {
    if (poprzedniProducent.current !== jedynyProducent) {
      poprzedniProducent.current = jedynyProducent
      if (f.series_in?.length > 0) setFilter('series_in', [])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jedynyProducent])

  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center gap-2 px-4 md:px-5 pt-4 pb-2">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-blue-50 text-blue-600">
          <FontAwesomeIcon icon={head.icon} className="text-sm" />
        </span>
        <h3 className="text-sm font-bold text-gray-900">{t(`sekcja.naglowek.${category}`)}</h3>
      </div>

      <div className="px-4 md:px-5 pb-4">
        {/* ---- Pola podstawowe ---- */}
        {category === 'panels' && (
          <div className="grid sm:grid-cols-3 gap-3 items-start">
            <div>
              {/* Zwykle opcjonalne, ale gdy wielkość instalacji podano LICZBĄ
                  MODUŁÓW, to z tego pola bierze się moc instalacji — i bez niego
                  dobór nie ma od czego zacząć. */}
              <label className={fieldLabel}>
                {t('sekcja.moduly.moc_modulu')}
                {state.tryb_mocy === 'sztuki' && (
                  <span className="text-blue-500" title={t('sekcja.moduly.moc_wymagana')}> *</span>
                )}
              </label>
              <input type="number" inputMode="numeric"
                placeholder={state.tryb_mocy === 'sztuki' ? t('sekcja.moduly.np_450') : dowolna}
                value={state.preferred_module_power_w}
                onChange={(e) => setField('preferred_module_power_w', e.target.value)}
                aria-invalid={state.tryb_mocy === 'sztuki' && !(Number(state.preferred_module_power_w) > 0)}
                className={numInput} />
              {state.tryb_mocy === 'sztuki' && !(Number(state.preferred_module_power_w) > 0) && (
                <p className="text-xs text-amber-600 mt-1 leading-4">
                  {t('sekcja.moduly.wzor')}
                </p>
              )}
            </div>
            <div>
              <label className={fieldLabel}>{t('sekcja.moduly.tolerancja')}</label>
              <Stepper value={state.power_tolerance_w} onChange={(v) => setField('power_tolerance_w', v)} step={5} />
            </div>
            <div>
              <label className={fieldLabel}>{t('sekcja.moduly.kierunek')}</label>
              <Segmented options={zTlumaczeniem(DIR_OPTIONS)} value={state.tolerance_direction} onChange={(v) => setField('tolerance_direction', v)} ariaLabel={t('sekcja.moduly.kierunek')} />
            </div>
          </div>
        )}

        {category === 'inverter' && (
          <>
            {/* Rodzaj falownika, wybór PODSTAWOWY (duże przyciski), nie filtr zaawansowany.
                Pojedynczy wybór; „Inne" kryje rzadsze rodzaje (dziś: mikroinwertery). */}
            <div className="mb-3">
              <label className={fieldLabel}>{t('sekcja.falownik.rodzaj')} <span className="text-blue-500" title={t('sekcja.pole_wymagane')}>*</span></label>
              <div className="grid grid-cols-2 gap-2">
                {['Sieciowy', 'Hybrydowy'].map((typ) => {
                  const active = (f.types_in[0] || '') === typ;
                  return (
                    <button key={typ} type="button" aria-pressed={active}
                      onClick={() => setFilter('types_in', [typ])}
                      className={`rounded-xl border-2 px-3 py-3 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                        active ? 'border-blue-600 bg-blue-600 text-white shadow-sm' : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300'
                      }`}>
                      {wart(typ)}
                      <span className={`block text-[10px] font-normal mt-0.5 ${active ? 'text-blue-100' : 'text-gray-400'}`}>
                        {typ === 'Hybrydowy' ? t('sekcja.falownik.z_magazynem') : t('sekcja.falownik.bez_magazynu')}
                      </span>
                    </button>
                  );
                })}
              </div>
              <details className="mt-1.5">
                <summary className="text-xs text-gray-500 cursor-pointer select-none hover:text-blue-600 list-none inline-flex items-center gap-1">
                  <FontAwesomeIcon icon={faChevronDown} className="text-[9px]" /> {t('sekcja.falownik.inne')}
                </summary>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {['Mikroinwertery'].map((typ) => {
                    const active = (f.types_in[0] || '') === typ;
                    return (
                      <button key={typ} type="button" aria-pressed={active}
                        onClick={() => setFilter('types_in', [typ])}
                        className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                          active ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-blue-300'
                        }`}>
                        {wart(typ)}
                      </button>
                    );
                  })}
                </div>
              </details>
              {batterySelected && (f.types_in[0] || '') === 'Sieciowy' && (
                <p className="mt-1.5 text-xs text-amber-600">{t('sekcja.falownik.wymaga_hybrydy')}</p>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className={fieldLabel}>{t('sekcja.falownik.moc_min')}</label>
                <input type="number" inputMode="decimal" value={state.power_kw_min} onChange={(e) => setField('power_kw_min', e.target.value)} className={numInput} />
              </div>
              <div>
                <label className={fieldLabel}>{t('sekcja.falownik.moc_max')}</label>
                <input type="number" inputMode="decimal" value={state.power_kw_max} onChange={(e) => setField('power_kw_max', e.target.value)} className={numInput} />
              </div>
            </div>
            {/* Skąd te liczby: bez wyjaśnienia podstawiony zakres wygląda na wzięty
                znikąd i pierwszym odruchem jest go skasować. */}
            <p className="mt-2 text-xs text-gray-500">
              {t('sekcja.falownik.zakres_opis')}
            </p>
          </>
        )}

        {category === 'battery' && (
          <>
            {/* ═══ DWA POZIOMY: SKRÓT NA GÓRZE, DOKŁADNE POLA POD SPODEM ═══
                Górny rząd to typowe pojemności jednym kliknięciem — ustawiają
                min I maks. na tę samą wartość, bo „chcę magazyn 10 kWh" to
                jedna liczba, a zakres wokół niej robi margines po prawej.
                Kto potrzebuje czegoś innego niż skok co 5 kWh, wpisuje wprost
                w polach niżej; skrót się wtedy odznacza. */}
            <div className="grid sm:grid-cols-2 gap-3 items-start">
              <div>
                <label className={fieldLabel}>{t('sekcja.magazyn.typowe')}</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {POJEMNOSCI_SKROTY.map((v) => {
                    const aktywny = Number(state.capacity_kwh_min) === v
                                 && Number(state.capacity_kwh_max) === v;
                    return (
                      <button key={v} type="button" aria-pressed={aktywny}
                        onClick={() => {
                          setField('capacity_kwh_min', String(v));
                          setField('capacity_kwh_max', String(v));
                        }}
                        className={`px-1.5 py-2 rounded-lg border text-xs font-semibold transition-colors
                                    focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                          aktywny
                            ? 'border-blue-500 bg-blue-600 text-white'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-blue-300 hover:bg-blue-50'
                        }`}>
                        {v} kWh
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Margines OBOK skrótów, nie pod nimi: skrót mówi „ile", margines
                  „z jakim luzem" — razem tworzą jedną decyzję i mają być widoczne
                  naraz. Mechanika ta sama co tolerancja mocy przy modułach,
                  żeby nie uczyć się dwóch wzorców w jednym formularzu. */}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={fieldLabel}>{t('sekcja.magazyn.margines')}</label>
                  <Stepper value={state.capacity_tolerance_kwh ?? 0}
                           onChange={(v) => setField('capacity_tolerance_kwh', v)} step={1} />
                </div>
                <div>
                  <label className={fieldLabel}>{t('sekcja.magazyn.kierunek')}</label>
                  <Segmented options={zTlumaczeniem(DIR_POJEMNOSCI)} value={state.tolerance_direction || 'both'}
                             onChange={(v) => setField('tolerance_direction', v)}
                             ariaLabel={t('sekcja.magazyn.kierunek_aria')} />
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <div>
                <label className={fieldLabel}>{t('sekcja.magazyn.pojemnosc_min')}</label>
                <input type="number" inputMode="decimal" value={state.capacity_kwh_min} onChange={(e) => setField('capacity_kwh_min', e.target.value)} className={numInput} />
              </div>
              <div>
                <label className={fieldLabel}>{t('sekcja.magazyn.pojemnosc_max')}</label>
                <input type="number" inputMode="decimal" value={state.capacity_kwh_max} onChange={(e) => setField('capacity_kwh_max', e.target.value)} className={numInput} />
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500 flex items-center gap-1.5">
              <span className="font-bold text-blue-500">~</span>
              {opisMarginesu(state, t)}
            </p>
            <p className="mt-1 text-xs text-gray-500 flex items-start gap-1.5">
              <FontAwesomeIcon icon={faBatteryFull} className="text-blue-500 mt-0.5" />
              {t('sekcja.magazyn.bms')}
            </p>
          </>
        )}

        {/* ---- Dobór zaawansowany (wyróżniony, zwinięty) ---- */}
        {/* BEZ `overflow-hidden`: rozwijana lista producentów jest pozycjonowana
            absolutnie i `overflow-hidden` na tym akordeonie ucinał ją o 190 px —
            wyglądała, jakby była schowana w oknie. Zmierzone. Zaokrąglenie tła
            nagłówka bierze teraz na siebie sam `summary` (`rounded-t-lg`, a przy
            zwiniętym akordeonie pełne `rounded-lg`), więc wygląd zostaje ten sam. */}
        <details className="mt-3 rounded-lg border border-gray-200 group">
          <summary className="flex items-center justify-between gap-2 px-3.5 py-3 text-sm font-semibold text-gray-800 cursor-pointer select-none list-none bg-gray-50 rounded-[7px] group-open:rounded-b-none group-open:bg-blue-50/70 group-open:border-b group-open:border-gray-200 hover:bg-gray-100 transition-colors">
            <span className="flex items-center gap-2">
              <FontAwesomeIcon icon={faSliders} className="text-blue-500" />
              {t('sekcja.zaawansowany.naglowek')} <span className="text-gray-400 font-normal">{t('sekcja.zaawansowany.opcjonalne')}</span>
            </span>
            <FontAwesomeIcon icon={faChevronDown} className="text-gray-400 text-xs transition-transform group-open:rotate-180" />
          </summary>
          {/* Filtry zaawansowane — dla zalogowanych. Bramka siedzi WEWNĄTRZ
              akordeonu, nie zamiast niego: rozwinięcie jest odpowiedzią na
              kliknięcie użytkownika, a zachęta pokazuje się dokładnie tam, gdzie
              spodziewał się pól. Zamknięcie całego akordeonu ukrywałoby fakt,
              że te filtry w ogóle istnieją. */}
          {/* Łączenie modułów magazynu (decyzja właściciela 2026-10-12): domyślnie włączone, więc stoi tutaj,
              nie przy pojemności, i bez opisu. Przy włączonym zakres pojemności dotyczy SUMY modułów. Poza bramką
              logowania: to nie filtr, tylko sposób czytania pojemności, i gość też może go wyłączyć. */}
          {category === 'battery' && (
            <label className="mx-3.5 mt-3 flex cursor-pointer items-center gap-2.5 text-sm font-semibold text-gray-800">
              <input type="checkbox" checked={Boolean(state.laczenie)}
                     onChange={(e) => setField('laczenie', e.target.checked)}
                     className="h-4 w-4 accent-blue-600" />
              {t('sekcja.magazyn.laczenie')}
            </label>
          )}
          <BramkaTresci
            maxWysokosc="max-h-72"
            tytul={t('sekcja.zaawansowany.bramka_tytul')}
            opis={t('sekcja.zaawansowany.bramka_opis')}
          >
          <div className="px-3.5 pb-4 pt-3 grid sm:grid-cols-2 gap-x-5 gap-y-4">
            <Modeable label={t('sekcja.zaawansowany.producent')} mode={m.manufacturer_in} onMode={(v) => setMode('manufacturer_in', v)}>
              <MultiSelect options={MANUFACTURERS[category]} value={f.manufacturer_in} onChange={(v) => setFilter('manufacturer_in', v)} searchable ariaLabel={t('sekcja.zaawansowany.producent')} />
            </Modeable>

            {jedynyProducent && (
              <div>
                <label className={fieldLabel}>
                  {t('sekcja.zaawansowany.seria')} <span className="text-gray-400 font-normal">({jedynyProducent})</span>
                </label>
                {seriesLoading ? (
                  <p className="text-xs text-gray-400 py-2">{t('sekcja.zaawansowany.wczytywanie')}</p>
                ) : seriesOptions.length === 0 ? (
                  <p className="text-xs text-gray-400 py-2 leading-snug">
                    {t('sekcja.zaawansowany.brak_serii')}
                  </p>
                ) : (
                  // Bez liczników przy nazwie — MultiSelect (współdzielony z resztą
                  // formularza) przyjmuje płaskie stringi jako opcje, tak jak
                  // wszystkie inne pola tutaj (Producent, Rodzaj, Fazy…).
                  <MultiSelect
                    options={seriesOptions.map((s) => s.value)}
                    value={f.series_in || []}
                    onChange={(v) => setFilter('series_in', v)}
                    searchable
                    ariaLabel={t('sekcja.zaawansowany.seria')}
                  />
                )}
              </div>
            )}

            {category === 'panels' && (
              <>
                <Modeable label={t('sekcja.zaawansowany.rodzaj')} mode={m.types_in} onMode={(v) => setMode('types_in', v)}>
                  <MultiSelect options={PANEL_TYPES} etykieta={wart} value={f.types_in} onChange={(v) => setFilter('types_in', v)} ariaLabel={t('sekcja.zaawansowany.rodzaj_modulu')} />
                </Modeable>
                <Modeable label={t('sekcja.zaawansowany.sprawnosc')} mode={m.min_efficiency} onMode={(v) => setMode('min_efficiency', v)}>
                  <input type="number" inputMode="decimal" placeholder={t('sekcja.zaawansowany.np_22')} value={f.min_efficiency} onChange={(e) => setFilter('min_efficiency', e.target.value)} className={numInput} />
                </Modeable>
                <Modeable label={t('sekcja.zaawansowany.obramowanie')} mode={m.frame_colour} onMode={(v) => setMode('frame_colour', v)}>
                  <div className="relative">
                    <select value={f.frame_colour} onChange={(e) => setFilter('frame_colour', e.target.value)} className={`appearance-none pr-8 ${numInput}`} aria-label={t('sekcja.zaawansowany.obramowanie')}>
                      <option value="">{t('sekcja.dowolne')}</option>
                      {FRAME_COLOURS.map((c) => <option key={c} value={c}>{wart(c)}</option>)}
                    </select>
                    <FontAwesomeIcon icon={faChevronDown} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                  </div>
                </Modeable>
                <div>
                  <label className={fieldLabel}>{t('sekcja.zaawansowany.gwarancja_produktowa')}</label>
                  <input type="number" inputMode="numeric" placeholder={dowolna} value={f.min_product_warranty} onChange={(e) => setFilter('min_product_warranty', e.target.value)} className={numInput} />
                </div>
                <div>
                  <label className={fieldLabel}>{t('sekcja.zaawansowany.dlugosc')}</label>
                  <div className="flex items-center gap-2">
                    <input type="number" inputMode="numeric" placeholder={t('sekcja.zaawansowany.od')} value={f.module_length_min} onChange={(e) => setFilter('module_length_min', e.target.value)} className={numInput} aria-label={t('sekcja.zaawansowany.dlugosc_od')} />
                    <span className="text-gray-400 text-xs">–</span>
                    <input type="number" inputMode="numeric" placeholder={t('sekcja.zaawansowany.do')} value={f.module_length_max} onChange={(e) => setFilter('module_length_max', e.target.value)} className={numInput} aria-label={t('sekcja.zaawansowany.dlugosc_do')} />
                  </div>
                </div>
                <div>
                  <label className={fieldLabel}>{t('sekcja.zaawansowany.grubosc')}</label>
                  <Segmented options={zDowolna(THICKNESS_OPTIONS)} value={f.module_thickness_range} onChange={(v) => setFilter('module_thickness_range', v)} ariaLabel={t('sekcja.zaawansowany.grubosc')} />
                </div>
                <div>
                  <label className={fieldLabel}>{t('sekcja.zaawansowany.waga')}</label>
                  <input type="number" inputMode="decimal" placeholder={dowolna} value={f.max_weight} onChange={(e) => setFilter('max_weight', e.target.value)} className={numInput} />
                </div>
                <div>
                  <label className={fieldLabel}>{t('sekcja.zaawansowany.cena_wp', { waluta: KRAJ.symbol })}</label>
                  <input type="number" inputMode="decimal" step="0.01" placeholder={t('sekcja.zaawansowany.np_cena_wp')} value={f.max_price_per_wp} onChange={(e) => setFilter('max_price_per_wp', e.target.value)} className={numInput} />
                </div>
                <div>
                  <label className={fieldLabel}>{t('sekcja.zaawansowany.cena_szt', { waluta: KRAJ.symbol })} <span className="text-blue-500" title={t('sekcja.zaawansowany.cena_szt_info')}>ⓘ</span></label>
                  <input type="number" inputMode="numeric" placeholder={t('sekcja.zaawansowany.na_modul')} value={f.max_price_per_unit} onChange={(e) => setFilter('max_price_per_unit', e.target.value)} className={numInput} />
                </div>
              </>
            )}

            {category === 'inverter' && (
              <>
                <div>
                  <label className={fieldLabel}>{t('sekcja.zaawansowany.fazy')} <span className="text-gray-400 font-normal">{t('sekcja.zaawansowany.fazy_domyslnie')}</span></label>
                  <MultiSelect options={INVERTER_PHASES} etykieta={wart} value={f.phase_in} onChange={(v) => setFilter('phase_in', v)} ariaLabel={t('sekcja.zaawansowany.fazy')} />
                </div>
                <div>
                  <label className={fieldLabel}>{t('sekcja.zaawansowany.mppt')}</label>
                  <Segmented options={zDowolna(MPPT_OPTIONS)} value={f.mppt_min} onChange={(v) => setFilter('mppt_min', v)} ariaLabel={t('sekcja.zaawansowany.mppt')} />
                </div>
                {/* Liczba falowników przy zakresie mocy, a nie w filtrach: to nie
                    jest warunek doboru produktu, tylko decyzja o kształcie
                    instalacji. Zakres mocy dotyczy JEDNEJ sztuki, więc „2 × 50 kW"
                    wpisuje się jako moc 50 i liczba 2. */}
                <div>
                  <label className={fieldLabel}>{t('sekcja.zaawansowany.liczba_falownikow')}</label>
                  <Stepper value={Number(state.quantity) || 1}
                           onChange={(v) => setField('quantity', Math.max(1, v))} step={1} />
                  <p className="mt-1 text-xs text-gray-500">
                    {t('sekcja.zaawansowany.liczba_falownikow_opis')}
                  </p>
                </div>
                <div>
                  <label className={fieldLabel}>{t('sekcja.zaawansowany.gwarancja')}</label>
                  <input type="number" inputMode="numeric" placeholder={dowolna} value={f.min_warranty} onChange={(e) => setFilter('min_warranty', e.target.value)} className={numInput} />
                </div>
                <div>
                  <label className={fieldLabel}>{t('sekcja.zaawansowany.cena_szt', { waluta: KRAJ.symbol })} <span className="text-blue-500" title={t('sekcja.zaawansowany.cena_szt_info')}>ⓘ</span></label>
                  <input type="number" inputMode="numeric" placeholder={t('sekcja.zaawansowany.na_falownik')} value={f.max_price_per_unit} onChange={(e) => setFilter('max_price_per_unit', e.target.value)} className={numInput} />
                </div>
              </>
            )}

            {category === 'battery' && (
              <>
                <Modeable label={t('sekcja.zaawansowany.napiecie')} mode={m.voltage_in} onMode={(v) => setMode('voltage_in', v)}>
                  <MultiSelect options={BATTERY_VOLTAGES} etykieta={wart} value={f.voltage_in} onChange={(v) => setFilter('voltage_in', v)} ariaLabel={t('sekcja.zaawansowany.napiecie')} />
                </Modeable>
                {/* „Min. gwarancja" usunięta z magazynów, w katalogu 0 z 1456 ofert ma tę
                    daną, więc filtr zawsze zwracałby pustkę. Falownik ją ma i tam została. */}
                <div>
                  <label className={fieldLabel}>{t('sekcja.zaawansowany.cena_kwh', { waluta: KRAJ.symbol })}</label>
                  <input type="number" inputMode="numeric" placeholder={dowolna} value={f.max_price_per_kwh} onChange={(e) => setFilter('max_price_per_kwh', e.target.value)} className={numInput} />
                </div>
                <div>
                  <label className={fieldLabel}>{t('sekcja.zaawansowany.cena_szt', { waluta: KRAJ.symbol })} <span className="text-blue-500" title={t('sekcja.zaawansowany.cena_szt_info')}>ⓘ</span></label>
                  <input type="number" inputMode="numeric" placeholder={t('sekcja.zaawansowany.na_magazyn')} value={f.max_price_per_unit} onChange={(e) => setFilter('max_price_per_unit', e.target.value)} className={numInput} />
                </div>
              </>
            )}
          </div>
          </BramkaTresci>
        </details>
      </div>
    </section>
  );
};

// memo: edycja jednej sekcji nie re-renderuje pozostałych (state[category] to osobne
// obiekty w reducerze — referencje innych kategorii się nie zmieniają).
export default React.memo(CategorySection);
